import { useState, useCallback, useRef, useMemo } from "react";
import html2canvas from "html2canvas-pro";
import { PATH } from "../../../constantes/constantes";
import ShareLineupImage from "../shareLineupImage/shareLineupImage";
import "./shareLineupButton.sass";

const HIDDEN_STYLE = {
  position: "fixed",
  left: "-9999px",
  top: 0,
  width: "1080px",
  height: "1920px",
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: -1,
  visibility: "hidden",
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook: toda la lógica de captura y share en un solo lugar.
// Retorna { handleShare, isCapturing, hiddenCanvas }.
// Úsalo UNA sola vez en el padre y pasa handleShare/isCapturing a los botones.
// ─────────────────────────────────────────────────────────────────────────────
export function useShareLineup({ slots, assignments, formationKey, config }) {
  const [isCapturing, setIsCapturing] = useState(false);
  const shareImageRef = useRef(null);

  const {
    shareUrl,
    shareText,
    shareTitle,
    shareFileName = "mi-alineacion-mejor-11.png",
  } = config;

  const SHARE_URL = PATH + "/" + shareUrl;

  const isAndroidWebView = useMemo(() => {
    const ua = navigator.userAgent;
    if (!/Android/.test(ua)) return false;
    return /wv/.test(ua) || (/Version\/\d/.test(ua) && !/SamsungBrowser/.test(ua));
  }, []);

  const isInIframe = window.self !== window.top;

  const generateImage = useCallback(async () => {
    const element = shareImageRef.current;
    if (!element) return null;

    element.style.visibility = "visible";

    const imgs = Array.from(element.querySelectorAll("img"));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => { img.onload = res; img.onerror = res; })
      )
    );
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: 1080,
        height: 1920,
        windowWidth: 1080,
        windowHeight: 1920,
        onclone: (clonedDoc) => {
          document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
            clonedDoc.head.appendChild(node.cloneNode(true));
          });
        },
      });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png", 1.0);
      });
    } catch (err) {
      console.error("[share-lineup] Error al capturar imagen:", err);
      return null;
    } finally {
      element.style.visibility = "hidden";
    }
  }, []);

  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }, []);

  const handleShare = useCallback(async () => {
    if (isCapturing) return;
    setIsCapturing(true);

    try {
      const blob = await generateImage();

      if (isInIframe && blob) {
        const base64 = await blobToBase64(blob);
        window.parent.postMessage({ type: "share-simulator", image: base64, title: shareTitle, text: shareText, url: SHARE_URL }, "*");
        return;
      }

      if (isAndroidWebView && blob) {
        const base64 = await blobToBase64(blob);
        if (window.Android?.share) {
          window.Android.share(base64, shareTitle, shareText, SHARE_URL);
          return;
        }
        window.postMessage({ type: "share-simulator", image: base64, title: shareTitle, text: shareText, url: SHARE_URL }, "*");
        return;
      }

      if (navigator.share && blob) {
        const file = new File([blob], shareFileName, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title: shareTitle, text: shareText, url: SHARE_URL, files: [file] });
          return;
        }
      }

      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: SHARE_URL });
        return;
      }

      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = shareFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("[share-lineup] error:", err.name, err.message);
    } finally {
      setIsCapturing(false);
    }
  }, [isCapturing, generateImage, blobToBase64, isInIframe, isAndroidWebView, shareTitle, shareText, SHARE_URL, shareFileName]);

  // Canvas oculto — debe montarse UNA sola vez en el DOM
  const hiddenCanvas = (
    <div ref={shareImageRef} style={HIDDEN_STYLE}>
      <ShareLineupImage {...{ slots, assignments, formationKey, config }} />
    </div>
  );

  return { handleShare, isCapturing, hiddenCanvas };
}

// ─────────────────────────────────────────────────────────────────────────────
// Botón visual puro: sin lógica, sin canvas oculto.
// Recibe onShare / isCapturing / accentColor desde el padre.
// ─────────────────────────────────────────────────────────────────────────────
export default function ShareLineupButton({ onShare, isCapturing, accentColor }) {
  const isHidden = useMemo(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || navigator.maxTouchPoints > 1;
    const isMac = /Macintosh|MacIntel/.test(ua);
    const isAndroid = /Android/.test(ua);
    const isAndroidWebView = isAndroid && (
      /wv/.test(ua) || (/Version\/\d/.test(ua) && !/SamsungBrowser/.test(ua))
    );

    // Ocultar en Android WebView
    if (isAndroidWebView) return true;
    // Ocultar en iOS/Mac con browsers que no soportan share de archivos
    if (!isIOS && !isMac) return false;
    if (isIOS) return /CriOS|FxiOS|OPiOS|EdgiOS|GSA/.test(ua);
    return /Chrome|Firefox|Edg|OPR/.test(ua);
  }, []);

  if (isHidden) return null;

  return (
    <button
      className={`share-lineup-btn ${isCapturing ? "capturing" : ""}`}
      style={accentColor ? { background: accentColor } : undefined}
      onClick={onShare}
      disabled={isCapturing}
      aria-label="Compartir mi mejor 11"
    >
      {isCapturing ? (
        <span className="share-lineup-btn__loader" />
      ) : (
        <svg className="share-lineup-btn__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46738 6.6793 8 5.7 8C4.20883 8 3 9.20883 3 10.7C3 12.1912 4.20883 13.4 5.7 13.4C6.6793 13.4 7.54305 12.9326 8.08261 12.2093L15.0227 16.0294C15.0077 16.1508 15 16.2745 15 16.4C15 18.0569 16.3431 19.4 18 19.4C19.6569 19.4 21 18.0569 21 16.4C21 14.7431 19.6569 13.4 18 13.4C17.0207 13.4 16.157 13.8674 15.6174 14.5907L8.67739 10.7706C8.69234 10.6492 8.7 10.5255 8.7 10.4C8.7 10.2745 8.69234 10.1508 8.67739 10.0294L15.6174 6.20929C16.157 6.93262 17.0207 7.4 18 7.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <span>{isCapturing ? "Capturando..." : "Compartir"}</span>
    </button>
  );
}
